import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { Toast } from '../../../services/Toast'

const apiUrl = process.env.REACT_APP_API_URL

const emptyForm = {
  title: '',
  desktopImage: '',
  mobileImage: '',
  linkUrl: '',
  sortOrder: 0,
  isActive: true,
}

const BannerScreen = () => {
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const [banners, setBanners] = useState([])
  const [formData, setFormData] = useState({ ...emptyForm })
  const [editingId, setEditingId] = useState(null)
  const [uploading, setUploading] = useState({ desktopImage: false, mobileImage: false })
  const [useSameImage, setUseSameImage] = useState(true)
  const [categoryQuery, setCategoryQuery] = useState('')
  const [categoryResults, setCategoryResults] = useState([])

  const isUploading = uploading.desktopImage || uploading.mobileImage

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${userInfo?.token}`,
    },
  })

  const resolveImageUrl = (path) => {
    if (!path) return ''
    if (/^https?:\/\//i.test(path)) return path
    const base = `${apiUrl || ''}`.replace(/\/+$/, '')
    const cleaned = `${path}`.replace(/^\/+/, '')
    return base ? `${base}/${cleaned}` : `/${cleaned}`
  }

  const fetchBanners = async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/api/banners/all`, authHeaders())
      setBanners(Array.isArray(data) ? data : [])
    } catch (error) {
      Toast({ type: 'error', message: 'Failed to load banners' })
    }
  }

  useEffect(() => {
    fetchBanners()
  }, [])

  const resetForm = () => {
    setFormData({ ...emptyForm })
    setEditingId(null)
    setCategoryQuery('')
    setCategoryResults([])
    setUseSameImage(true)
  }

  const handleUpload = async (file, target) => {
    if (!file) return
    try {
      setUploading((prev) => ({ ...prev, [target]: true }))
      const form = new FormData()
      form.append('image', file)
      const { data } = await axios.post(`${apiUrl}/api/banner-upload`, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userInfo?.token}`,
        },
      })
      const path = data?.path
      if (!path) {
        throw new Error('Upload failed')
      }
      setFormData((prev) => ({
        ...prev,
        [target]: path,
      }))
    } catch (error) {
      Toast({ type: 'error', message: 'Banner image upload failed' })
    } finally {
      setUploading((prev) => ({ ...prev, [target]: false }))
    }
  }

  const submitHandler = async (event) => {
    event.preventDefault()
    if (isUploading) {
      Toast({ type: 'error', message: 'Please wait for image upload to finish' })
      return
    }

    // If "same image" is on, mirror desktop into mobile. Otherwise allow blank mobile to fall back to desktop.
    const effectiveMobile = useSameImage || !formData.mobileImage ? formData.desktopImage : formData.mobileImage
    const payload = { ...formData, mobileImage: effectiveMobile }

    if (!payload.desktopImage) {
      Toast({ type: 'error', message: 'Desktop image is required' })
      return
    }

    try {
      if (editingId) {
        await axios.put(
          `${apiUrl}/api/banners/${editingId}`,
          payload,
          authHeaders()
        )
        Toast({ type: 'success', message: 'Banner updated' })
      } else {
        await axios.post(`${apiUrl}/api/banners`, payload, authHeaders())
        Toast({ type: 'success', message: 'Banner created' })
      }
      resetForm()
      fetchBanners()
    } catch (error) {
      Toast({ type: 'error', message: 'Failed to save banner' })
    }
  }

  const editBanner = (banner) => {
    setFormData({
      title: banner.title || '',
      desktopImage: banner.desktopImage || '',
      mobileImage: banner.mobileImage || '',
      linkUrl: banner.linkUrl || '',
      sortOrder: banner.sortOrder || 0,
      isActive: banner.isActive ?? true,
    })
    setEditingId(banner._id)
    setUseSameImage(
      Boolean(banner.desktopImage) &&
        (!banner.mobileImage || banner.mobileImage === banner.desktopImage)
    )
  }

  const deleteBanner = async (bannerId) => {
    if (!window.confirm('Delete this banner?')) return
    try {
      await axios.delete(`${apiUrl}/api/banners/${bannerId}`, authHeaders())
      Toast({ type: 'success', message: 'Banner deleted' })
      fetchBanners()
    } catch (error) {
      Toast({ type: 'error', message: 'Failed to delete banner' })
    }
  }

  const searchCategories = async (value) => {
    setCategoryQuery(value)
    if (!value) {
      setCategoryResults([])
      return
    }
    try {
      const { data } = await axios.get(`${apiUrl}/api/category/name/${value}`)
      setCategoryResults(Array.isArray(data) ? data : [])
    } catch (error) {
      setCategoryResults([])
    }
  }

  const selectCategory = (category) => {
    if (!category?.slug) return
    setFormData((prev) => ({
      ...prev,
      linkUrl: `/categories/${category.slug}`,
    }))
    setCategoryQuery(category.name)
    setCategoryResults([])
  }

  return (
    <section className='minHeight100'>
      <div className='container'>
        <div className='row'>
          <div className='col-lg-8'>
            <h3 className='commonTitle--xl my-4'>Homepage Banners</h3>
            <div className='card mb-4'>
              <div className='card-body'>
                <form onSubmit={submitHandler}>
                  <div className='mb-3'>
                    <label className='form-label'>Title (optional)</label>
                    <input
                      type='text'
                      className='form-control'
                      value={formData.title}
                      onChange={(e) => {
                        const value = e.target.value
                        setFormData((prev) => ({ ...prev, title: value }))
                      }}
                    />
                  </div>

                  <div className='mb-3'>
                    <label className='form-label'>Category link (search)</label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Search category name'
                      value={categoryQuery}
                      onChange={(e) => {
                        const value = e.target.value
                        searchCategories(value)
                      }}
                    />
                    {categoryResults.length > 0 && (
                      <div className='border rounded mt-2 p-2'>
                        {categoryResults.map((item) => (
                          <button
                            key={item._id}
                            type='button'
                            className='btn btn-link d-block text-start'
                            onClick={() => selectCategory(item)}
                          >
                            {item.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className='mb-3'>
                    <label className='form-label'>Link URL</label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='/categories/mobiles'
                      value={formData.linkUrl}
                      onChange={(e) => {
                        const value = e.target.value
                        setFormData((prev) => ({ ...prev, linkUrl: value }))
                      }}
                    />
                  </div>

                  <div className='mb-2 form-check form-switch'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='useSameImage'
                      checked={useSameImage}
                      onChange={(e) => setUseSameImage(e.target.checked)}
                    />
                    <label className='form-check-label' htmlFor='useSameImage'>
                      Use the same image for desktop and mobile
                    </label>
                  </div>

                  <div className='row'>
                    <div className={useSameImage ? 'col-12 mb-3' : 'col-md-6 mb-3'}>
                      <label className='form-label'>
                        {useSameImage ? 'Banner Image' : 'Desktop Image'}
                      </label>
                      <input
                        type='file'
                        className='form-control'
                        accept='image/*'
                        onChange={(e) => handleUpload(e.target.files?.[0], 'desktopImage')}
                      />
                      {uploading.desktopImage && <div className='mt-2'>Uploading...</div>}
                      {formData.desktopImage && (
                        <img
                          src={resolveImageUrl(formData.desktopImage)}
                          alt='Desktop preview'
                          className='img-fluid mt-2'
                        />
                      )}
                    </div>

                    {!useSameImage && (
                      <div className='col-md-6 mb-3'>
                        <label className='form-label'>Mobile Image</label>
                        <input
                          type='file'
                          className='form-control'
                          accept='image/*'
                          onChange={(e) => handleUpload(e.target.files?.[0], 'mobileImage')}
                        />
                        {uploading.mobileImage && <div className='mt-2'>Uploading...</div>}
                        {formData.mobileImage && (
                          <img
                            src={resolveImageUrl(formData.mobileImage)}
                            alt='Mobile preview'
                            className='img-fluid mt-2'
                          />
                        )}
                        <small className='text-muted d-block mt-1'>
                          Leave empty to use the desktop image on mobile too.
                        </small>
                      </div>
                    )}
                  </div>

                  <div className='row'>
                    <div className='col-md-4 mb-3'>
                      <label className='form-label'>Sort Order</label>
                      <input
                        type='number'
                        className='form-control'
                        value={formData.sortOrder}
                        onChange={(e) => {
                          const value = e.target.value
                          setFormData((prev) => ({
                            ...prev,
                            sortOrder: value,
                          }))
                        }}
                      />
                    </div>
                    <div className='col-md-4 mb-3 d-flex align-items-end'>
                      <div className='form-check'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={formData.isActive}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              isActive: e.target.checked,
                            }))
                          }
                          id='bannerActive'
                        />
                        <label className='form-check-label' htmlFor='bannerActive'>
                          Active
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className='d-flex gap-2'>
                    <button type='submit' className='btn btn-success' disabled={isUploading}>
                      {isUploading
                        ? 'Uploading image…'
                        : editingId
                        ? 'Update Banner'
                        : 'Create Banner'}
                    </button>
                    {editingId && (
                      <button
                        type='button'
                        className='btn btn-secondary'
                        onClick={resetForm}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='col-12'>
            <h4 className='commonTitle--xl mb-3'>Existing Banners</h4>
          </div>
          {banners.map((banner) => (
            <div className='col-lg-4' key={banner._id}>
              <div className='card mb-4'>
                <div className='card-body'>
                  <div className='mb-2'>
                    <strong>{banner.title || 'Untitled banner'}</strong>
                  </div>
                  <div className='mb-2'>Link: {banner.linkUrl || '—'}</div>
                  <div className='mb-2'>Order: {banner.sortOrder ?? 0}</div>
                  <div className='mb-2'>Status: {banner.isActive ? 'Active' : 'Inactive'}</div>
                  {banner.desktopImage && (
                    <div className='mb-2'>
                      {banner.mobileImage && banner.mobileImage !== banner.desktopImage && (
                        <small className='text-muted d-block mb-1'>Desktop</small>
                      )}
                      <img
                        src={resolveImageUrl(banner.desktopImage)}
                        alt='Desktop banner'
                        className='img-fluid'
                      />
                    </div>
                  )}
                  {banner.mobileImage && banner.mobileImage !== banner.desktopImage && (
                    <div className='mb-2'>
                      <small className='text-muted d-block mb-1'>Mobile</small>
                      <img
                        src={resolveImageUrl(banner.mobileImage)}
                        alt='Mobile banner'
                        className='img-fluid'
                      />
                    </div>
                  )}
                  <div className='d-flex gap-2'>
                    <button
                      className='btn btn-outline-primary btn-sm'
                      onClick={() => editBanner(banner)}
                    >
                      Edit
                    </button>
                    <button
                      className='btn btn-outline-danger btn-sm'
                      onClick={() => deleteBanner(banner._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {banners.length === 0 && (
            <div className='col-12'>
              <div className='alert alert-light'>No banners yet.</div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default BannerScreen
